import { createEffect } from 'effector';
import { ghApi } from 'services/DataService';
import type { CampaignSetupJobOption, CampaignSetupOption, CampaignSetupServiceSelection } from './types';

type ApiOption = {
  id?: number | string | null;
  label?: string | null;
};

type ApiJobOption = {
  id?: number | string | null;
  name?: string | null;
  number?: string | null;
  label?: string | null;
};

const getClientOptionsFx = createEffect<void, CampaignSetupOption[]>(async () => {
  return ghApi.get('/campaigns/setup/clients').then(response => response.data.map(mapOptionFromApi));
});

const getCloneCampaignOptionsFx = createEffect<{ clientId: string }, CampaignSetupJobOption[]>(async ({ clientId }) => {
  return ghApi.get('/campaigns/setup/jobs', { params: { clientId, includeNone: true } }).then(response => response.data.map(mapJobOptionFromApi));
});

const getEditCampaignOptionsFx = createEffect<{ clientId: string }, CampaignSetupJobOption[]>(async ({ clientId }) => {
  return ghApi.get('/campaigns/setup/jobs', { params: { clientId } }).then(response => response.data.map(mapJobOptionFromApi));
});

const getContactOptionsFx = createEffect<{ clientId: string }, CampaignSetupOption[]>(async ({ clientId }) => {
  return ghApi.get('/campaigns/setup/contacts', { params: { clientId } }).then(response => response.data.map(mapOptionFromApi));
});

const getLobOptionsFx = createEffect<{ clientId: string }, CampaignSetupOption[]>(async ({ clientId }) => {
  return ghApi.get('/campaigns/setup/lobs', { params: { clientId } }).then(response => response.data.map(mapOptionFromApi));
});

const getMailerIdOptionsFx = createEffect<{ clientId: string }, CampaignSetupOption[]>(async ({ clientId }) => {
  return ghApi.get('/campaigns/setup/mailer-ids', { params: { clientId } }).then(response => response.data.map(mapOptionFromApi));
});

const getJobContactFx = createEffect<{ jobId: string }, CampaignSetupOption>(async ({ jobId }) => {
  return ghApi.get(`/campaigns/setup/jobs/${jobId}/contact`).then(response => mapOptionFromApi(response.data));
});

const getJobLobFx = createEffect<{ jobId: string }, CampaignSetupOption>(async ({ jobId }) => {
  return ghApi.get(`/campaigns/setup/jobs/${jobId}/lob`).then(response => mapOptionFromApi(response.data));
});

const getJobServicesFx = createEffect<{ jobId: string }, CampaignSetupServiceSelection>(async ({ jobId }) => {
  return ghApi.get(`/campaigns/setup/jobs/${jobId}/services`).then(response => response.data);
});

const submitCreateFx = createEffect<object, void>(async data => {
  await ghApi.post('/admin/campaigns', data);
});

const submitEditFx = createEffect<object, void>(async data => {
  await ghApi.put('/admin/campaigns', data);
});

export const $$campaignSetupService = {
  getClientOptionsFx,
  getCloneCampaignOptionsFx,
  getEditCampaignOptionsFx,
  getContactOptionsFx,
  getLobOptionsFx,
  getMailerIdOptionsFx,
  getJobContactFx,
  getJobLobFx,
  getJobServicesFx,
  submitCreateFx,
  submitEditFx
};

function mapOptionFromApi(option: ApiOption): CampaignSetupOption {
  return {
    id: String(option?.id ?? ''),
    label: option?.label ?? ''
  };
}

function mapJobOptionFromApi(option: ApiJobOption): CampaignSetupJobOption {
  return {
    id: String(option?.id ?? ''),
    name: option?.name ?? '',
    number: option?.number ?? '',
    label: option?.label ?? ''
  };
}
